/**
 * User Management Service (Admin)
 *
 * Provides methods for system administrators to manage users
 */

import api from '../api';
import type {
  AdminUser,
  PaginatedResponse,
  UserListParams,
  UpdateUserData,
  UserResponse,
  MessageResponse,
} from '../../types/admin';

/**
 * Get paginated list of users with filters
 */
export async function getAllUsers(
  params?: UserListParams
): Promise<PaginatedResponse<AdminUser>> {
  const response = await api.get<PaginatedResponse<AdminUser>>('/api/admin/users', {
    params,
  });
  return response.data;
}

/**
 * Get single user by ID with account relationship
 */
export async function getUserById(id: number): Promise<AdminUser> {
  const response = await api.get<{ user: AdminUser }>(`/api/admin/users/${id}`);
  return response.data.user;
}

/**
 * Update user details (admin)
 */
export async function updateUser(
  id: number,
  data: UpdateUserData
): Promise<AdminUser> {
  const response = await api.put<UserResponse>(`/api/admin/users/${id}`, data);
  return response.data.user;
}

/**
 * Delete user permanently
 */
export async function deleteUser(id: number): Promise<MessageResponse> {
  const response = await api.delete<MessageResponse>(`/api/admin/users/${id}`);
  return response.data;
}

/**
 * Disable user account
 */
export async function disableUser(id: number): Promise<AdminUser> {
  const response = await api.post<UserResponse>(`/api/admin/users/${id}/disable`);
  return response.data.user;
}

/**
 * Enable user account
 */
export async function enableUser(id: number): Promise<AdminUser> {
  const response = await api.post<UserResponse>(`/api/admin/users/${id}/enable`);
  return response.data.user;
}

/**
 * Verify user's email address (admin only)
 */
export async function verifyEmail(id: number): Promise<AdminUser> {
  const response = await api.post<UserResponse>(`/api/admin/users/${id}/verify-email`);
  return response.data.user;
}

export interface UserLoginIp {
  ip_address: string;
  fingerprint: string | null;
  login_count: number;
  event: string;
  first_seen_at: string;
  last_seen_at: string;
}

/**
 * Get login IP history for a user (admin only)
 */
export async function getUserLoginIps(userId: number): Promise<UserLoginIp[]> {
  const response = await api.get<{ ips: UserLoginIp[] }>(`/api/admin/users/${userId}/login-ips`);
  return response.data.ips;
}

/**
 * User management service object
 */
export const userManagementService = {
  getAllUsers,
  getUserById,
  updateUser,
  deleteUser,
  disableUser,
  enableUser,
  verifyEmail,
  getUserLoginIps,
};
